import { createSelector } from '@reduxjs/toolkit';
import { isSameCompany, matchKey } from '../../utils/smeIpos';

/**
 * Company logos, keyed by company name, from whatever detail records the store
 * already holds.
 *
 * A logo only ever arrives inside an extracted filing
 * (json_data.basic_company_details.logo_url), never in the market feeds that
 * drive the GMP, subscription and upcoming boards, and the file's extension
 * varies (.png/.webp/.jpg/.ico) so its URL can't be derived from the company
 * name either. This reads the details a page has already loaded rather than
 * fetching more: a board shows logos for the issuers it happens to know and
 * initials for the rest.
 */
const withLogos = (details) =>
  details.filter((detail) => detail?.logoUrl && detail.companyName).map((detail) => ({ key: matchKey(detail.companyName), name: detail.companyName, logoUrl: detail.logoUrl }));

export const selectCompanyLogos = createSelector(
  [(state) => state.ipoCatalog.records, (state) => state.drhp.detailsById, (state) => state.drhpFiling.byId],
  (catalogRecords, drhpDetails, filings) => {
    const entries = [
      ...withLogos(catalogRecords || []),
      ...withLogos(Object.values(drhpDetails || {})),
      ...Object.values(filings || {})
        .filter((filing) => filing?.logoUrl && filing.company)
        .map((filing) => ({ key: matchKey(filing.company), name: filing.company, logoUrl: filing.logoUrl })),
    ];

    const byKey = new Map();
    entries.forEach((entry) => {
      if (entry.key && !byKey.has(entry.key)) byKey.set(entry.key, entry);
    });

    return {
      /** Exact-ish lookup first, then the prefix match that joins feed names to filed names */
      lookup: (company) => {
        const key = matchKey(company);
        if (!key) return null;
        const direct = byKey.get(key);
        if (direct) return direct.logoUrl;
        for (const entry of byKey.values()) {
          if (isSameCompany(entry.name, company)) return entry.logoUrl;
        }
        return null;
      },
      size: byKey.size,
    };
  }
);

export default selectCompanyLogos;
